<template>
  <div class="expert-opinion" v-if="slides.length">
    <div class="expert-opinion__header" @click="emitMore">
      <h2>{{ sectionTitle }}</h2>
      <a>MORE></a>
    </div>
    <el-carousel
      ref="carousel"
      class="expert-opinion__carousel"
      arrow="never"
      indicator-position="none"
      @change="handleChange"
    >
      <el-carousel-item v-for="(group, index) in slides" :key="index">
        <div
          class="expert-opinion__grid"
          :class="`expert-opinion__grid--${Math.min(group.length, 3)}`"
        >
          <article
            v-for="item in group"
            :key="item.id"
            class="expert-opinion__card"
            @click="emitSelect(item)"
          >
            <div class="expert-opinion__image">
              <img :src="getImage(item)" :alt="getAuthor(item) || item.title_en || item.title || sectionTitle">
            </div>
            <div class="expert-opinion__content">
              <i class="expert-opinion__quote"></i>
              <p class="expert-opinion__author" v-if="getAuthor(item)">{{ getAuthor(item) }}：</p>
              <span class="expert-opinion__desc">{{ getSummary(item) }}</span>
              <time class="expert-opinion__date">{{ item.created_at ? item.created_at.slice(0, 10) : "" }}</time>
            </div>
          </article>
        </div>
      </el-carousel-item>
    </el-carousel>
    <ul class="expert-opinion__dots" v-if="slides.length > 1">
      <li
        v-for="(_, index) in slides"
        :key="index"
        :class="{ active: activeIndex === index }"
        @click="setActive(index)"
      ></li>
    </ul>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref, watch } from "vue"
import fallbackImage from "@/assets/expert-opinion-placeholder.svg"

const emits = defineEmits(["more", "select"])
const props = defineProps({
  data: {
    type: Object,
    default: () => ({})
  }
})

const baseUrl = `${process.env.VUE_APP_BASE_URL}/`
const carousel = ref(null)
const activeIndex = ref(0)
const sectionTitle = computed(() => props.data?.title_en || props.data?.title || "专家观点")
const slides = computed(() => {
  if (!Array.isArray(props.data?.item)) return []
  return props.data.item
    .map((group) => Array.isArray(group) ? group.filter(Boolean) : [group].filter(Boolean))
    .filter((group) => group.length)
})

const getImage = (item) => {
  if (!item?.thumb) return fallbackImage
  return item.thumb.indexOf("http") !== -1 ? item.thumb : `${baseUrl}${item.thumb}`
}
const getAuthor = (item) => {
  return item?.author_en || item?.author || ""
}
const getSummary = (item) => {
  return item?.description_en || item?.description || item?.title_en || item?.title || ""
}
const handleChange = (index) => {
  activeIndex.value = index
}
const setActive = (index) => {
  activeIndex.value = index
  carousel.value?.setActiveItem(index)
}
const emitMore = () => {
  emits("more", props.data)
}
const emitSelect = (item) => {
  emits("select", item)
}

watch(() => slides.value.length, () => {
  activeIndex.value = 0
})
</script>

<style lang="less" scoped>
@maxw: 128rem;

.expert-opinion {
  position: relative;
  width: 1588/@maxw;
  margin: 118/@maxw auto 110/@maxw;
  cursor: pointer;

  &__header {
    width: 100%;
    margin-bottom: 15/@maxw;
    padding-bottom: 18/@maxw;
    display: flex;
    justify-content: space-between;
    border-bottom: 4/@maxw solid transparent;
    border-image: linear-gradient(to right, #174994, #387DE2) 0 0 100 0;

    h2 {
      margin: 0;
      font-size: 39/@maxw;
      font-family: fantBold;
      font-weight: 300;
      color: #174994;
      -webkit-font-smoothing: subpixel-antialiased;
    }

    a {
      margin-top: 10/@maxw;
      font-size: 18/@maxw;
      line-height: 60/@maxw;
      font-family: Source Han Sans CN-Medium;
      font-weight: 800;
      color: #B19A86;
    }
  }

  &__carousel {
    width: 100%;
    overflow: hidden;

    :deep(.el-carousel__container) {
      height: 292/@maxw;
    }
  }

  &__grid {
    margin-top: 20/@maxw;
    display: flex;
    justify-content: center;
    gap: 28/@maxw;
  }

  &__grid--2,
  &__grid--3 {
    justify-content: space-between;
  }

  &__card {
    display: flex;
    width: 496/@maxw;
    min-width: 496/@maxw;
    height: 251/@maxw;
    overflow: hidden;
    transition: all .35s ease;

    &:hover .expert-opinion__image img {
      transform: scale(1.08);
    }

    &:hover .expert-opinion__content {
      background: url("../../assets/kyzsback2.jpg");
      background-repeat: no-repeat;
      background-size: 100% 100%;
    }

    &:hover .expert-opinion__author,
    &:hover .expert-opinion__desc,
    &:hover .expert-opinion__date {
      color: #174994;
    }
  }

  &__image {
    width: 205/@maxw;
    height: 100%;
    flex-shrink: 0;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transform: scale(1.02);
      transition: all .3s;
    }
  }

  &__content {
    position: relative;
    flex: 1;
    min-width: 0;
    height: 100%;
    padding: 0 30/@maxw;
    background: url("../../assets/kyzsback3.jpg");
    background-repeat: no-repeat;
    background-size: 100% 100%;
    transition: all .3s;
    overflow: hidden;
  }

  &__quote {
    position: absolute;
    top: 22/@maxw;
    right: 22/@maxw;
    width: 35/@maxw;
    height: 35/@maxw;
    background-image: url("../../assets/yinhao.png");
    background-repeat: no-repeat;
    background-size: 100% 100%;
  }

  &__author {
    margin: 57/@maxw 0 0;
    font-size: 24/@maxw;
    font-family: Source Han Sans CN-Medium;
    font-weight: 700;
    color: #FFFFFF;
  }

  &__desc {
    margin-top: 10/@maxw;
    display: -webkit-box;
    word-break: break-all;
    text-overflow: ellipsis;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    font-size: 24/@maxw;
    line-height: 1.8;
    font-family: Source Han Sans CN-Medium;
    font-weight: 700;
    color: #FFFFFF;
  }

  &__date {
    float: right;
    margin-right: -15/@maxw;
    font-size: 20/@maxw;
    font-family: Source Han Sans CN-Medium;
    font-weight: 500;
    color: #FFFFFF;
  }

  &__dots {
    margin: 30/@maxw 0 0;
    padding: 0;
    display: flex;
    justify-content: center;
    list-style: none;

    li {
      width: 13/@maxw;
      height: 13/@maxw;
      margin-right: 15/@maxw;
      border-radius: 50%;
      border: 2/@maxw solid #B19A86;
      box-sizing: border-box;
      cursor: pointer;

      &.active {
        background-color: #B19A86;
      }
    }

    li:last-child {
      margin-right: 0;
    }
  }
}

@media screen and (max-width:750px) {
  @maxw: 50rem;

  .expert-opinion {
    width: 655/@maxw;
    margin: 72/@maxw auto 90/@maxw;

    &__header {
      margin-bottom: 12/@maxw;
      padding-bottom: 16/@maxw;

      h2 {
        font-size: 39/@maxw;
      }

      a {
        margin-top: 12/@maxw;
        font-size: 18/@maxw;
        line-height: 1.2;
      }
    }

    &__carousel {
      :deep(.el-carousel__container) {
        height: 835/@maxw;
      }
    }

    &__grid {
      margin-top: 10/@maxw;
      flex-direction: column;
      gap: 22/@maxw;
    }

    &__card {
      height: 250/@maxw;
    }

    &__image {
      width: 210/@maxw;
    }

    &__content {
      padding: 0 24/@maxw;
    }

    &__quote {
      top: 18/@maxw;
      right: 18/@maxw;
      width: 34/@maxw;
      height: 34/@maxw;
    }

    &__author,
    &__desc {
      font-size: 24/@maxw;
    }

    &__author {
      margin-top: 44/@maxw;
    }

    &__date {
      font-size: 18/@maxw;
      margin-right: 0;
    }

    &__dots {
      margin-top: 24/@maxw;
    }
  }
}
</style>
